import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useAppFeatures } from '../queries/appFeatures';

interface AppFeature {
  key: string;
  enabled: boolean;
  label: string;
  description?: string;
}

interface FeatureFlagsContextValue {
  flags: Record<string, boolean>;
  features: AppFeature[];
  loaded: boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export const FEATURE_KEYS = {
  simulatorMaster: 'simulator_master',
  portfolioMaster: 'portfolio_master',
  onboardingEtfFundNotice: 'onboarding_etf_fund_notice',
} as const;

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  // Fail-open: on error TanStack Query preserves the last successful data,
  // so a transient backend issue does not lock users out of features.
  const { data, status, refetch } = useAppFeatures(!!user);

  const features = (data ?? []) as AppFeature[];
  const loaded = !user ? false : status !== 'pending';

  const refresh = useCallback(async () => {
    if (!user) return;
    await refetch();
  }, [user, refetch]);

  const flags = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const f of features) map[f.key] = f.enabled;
    return map;
  }, [features]);

  const value = useMemo(
    () => ({ flags, features, loaded, refresh }),
    [flags, features, loaded, refresh],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return ctx;
};

export const useFeatureFlag = (key: string): boolean => {
  const { flags, loaded } = useFeatureFlags();
  if (!loaded) return true;
  return flags[key] !== false;
};
